"use client";

import React, { useState } from "react";
import Link from "next/link";
import { Phone, ShieldCheck, Mail, MapPin, Send, LoaderCircle } from "lucide-react";
import toast from "react-hot-toast";
import { apiRequest } from "../utils/api";

export const Footer: React.FC = () => {
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [newsletterMessage, setNewsletterMessage] = useState("");

  const handleNewsletterSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const email = newsletterEmail.trim().toLowerCase();
    if (!email) return;

    setIsSubscribing(true);
    setNewsletterMessage("");

    try {
      const response = await apiRequest("/newsletter/subscribe", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      const message = response.message || "Thanks for subscribing to our newsletter.";
      setNewsletterMessage(message);
      setNewsletterEmail("");
      toast.success(message);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : "Unable to subscribe right now.";
      setNewsletterMessage(message);
      toast.error(message);
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <footer className="bg-zinc-950 text-zinc-400 border-t border-zinc-900 mt-auto">
      <div className="mx-auto max-w-7xl px-6 py-12 md:py-16 lg:px-8">
        <div className="xl:grid xl:grid-cols-3 xl:gap-8">
          {/* Brand Info */}
          <div className="space-y-6">
            <Link href="/" className="flex items-center gap-2 text-white font-bold text-xl tracking-tight">
              <span className="bg-gradient-to-r from-violet-500 to-indigo-500 text-transparent bg-clip-text font-black">
                TORONGO
              </span>
              <span className="text-zinc-200">GADGETS</span>
            </Link>
            <p className="text-sm max-w-xs leading-6">
              Your ultimate destination for cutting-edge electronics, smart wearables, high-performance laptops, and premium audio accessories.
            </p>
            <div className="flex gap-4">
              <span className="flex items-center gap-2 text-xs text-zinc-500">
                <ShieldCheck className="h-4 w-4 text-emerald-500" />
                100% Genuine Products
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div className="mt-10 grid grid-cols-2 gap-8 xl:col-span-2 xl:mt-0">
            <div className="md:grid md:grid-cols-2 md:gap-8">
              <div>
                <h3 className="text-sm font-semibold text-zinc-200 uppercase tracking-wider">Shop Categories</h3>
                <ul role="list" className="mt-4 space-y-2 text-sm">
                  <li>
                    <Link href="/shop?category=Smartphones" className="hover:text-white transition-colors">Smartphones</Link>
                  </li>
                  <li>
                    <Link href="/shop?category=Laptops" className="hover:text-white transition-colors">Laptops</Link>
                  </li>
                  <li>
                    <Link href="/shop?category=Audio" className="hover:text-white transition-colors">Audio & Sound</Link>
                  </li>
                  <li>
                    <Link href="/shop?category=Wearables" className="hover:text-white transition-colors">Wearables</Link>
                  </li>
                </ul>
              </div>
              <div className="mt-10 md:mt-0">
                <h3 className="text-sm font-semibold text-zinc-200 uppercase tracking-wider">Support</h3>
                <ul role="list" className="mt-4 space-y-2 text-sm">
                  <li>
                    <Link href="/dashboard" className="hover:text-white transition-colors">My Profile</Link>
                  </li>
                  <li>
                    <Link href="/cart" className="hover:text-white transition-colors">Shopping Cart</Link>
                  </li>
                  <li>
                    <Link href="/dashboard" className="hover:text-white transition-colors">Order History</Link>
                  </li>
                  <li>
                    <a href="mailto:support@torongo.com" className="hover:text-white transition-colors">Contact Support</a>
                  </li>
                </ul>
              </div>
            </div>

            {/* Newsletter / Contact */}
            <div>
              <h3 className="text-sm font-semibold text-zinc-200 uppercase tracking-wider">Contact Info</h3>
              <ul className="mt-4 space-y-3 text-sm">
                <li className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 shrink-0 text-indigo-500" />
                  <span>Dhaka, Bangladesh</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="h-4 w-4 shrink-0 text-indigo-500" />
                  <span>+8801788895287</span>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="h-4 w-4 shrink-0 text-indigo-500" />
                  <span>support@torongo.com</span>
                </li>
              </ul>

              <div className="mt-6">
                <h4 className="text-xs font-semibold text-zinc-300 uppercase tracking-wider">Subscribe to Newsletter</h4>
                <form onSubmit={handleNewsletterSubmit} className="mt-2 flex max-w-md gap-2">
                  <input
                    type="email"
                    required
                    aria-label="Newsletter email address"
                    suppressHydrationWarning
                    placeholder="Enter your email"
                    value={newsletterEmail}
                    onChange={(event) => {
                      setNewsletterEmail(event.target.value);
                      setNewsletterMessage("");
                    }}
                    disabled={isSubscribing}
                    className="w-full min-w-0 flex-auto rounded bg-zinc-900 border border-zinc-800 px-3.5 py-1.5 text-sm text-white shadow-sm ring-1 ring-inset ring-transparent focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    type="submit"
                    disabled={isSubscribing || !newsletterEmail.trim()}
                    aria-label="Subscribe to newsletter"
                    className="flex items-center justify-center rounded bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubscribing ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </button>
                </form>
                <p aria-live="polite" className="mt-2 min-h-4 text-xs text-zinc-500">
                  {newsletterMessage}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 border-t border-zinc-900 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-xs">
          <p>&copy; {new Date().getFullYear()} Torongo Gadgets. All rights reserved.</p>
          <div className="flex gap-6 text-zinc-500">
            <Link href="/terms" className="hover:text-zinc-300 transition-colors">Terms of Service</Link>
            <Link href="/privacy" className="hover:text-zinc-300 transition-colors">Privacy Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
export default Footer;
